// src/pages/LoginPage.js
import React, { useEffect, useState } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import {
  signInWithGoogle,
  signInWithApple,
  consumePendingRedirectResult,
} from '../services/auth.providers';
import ApiService from '../services/api.service';
import './LoginPage.css';

const DOT_COLORS = ['#10b981', '#f59e0b', '#ef4444'];

const WORDWALL_PHRASE = 'the ambitious, hustlers, innovators, dreamers, coaches, creators, builders, champions, leaders, trailblazers, go-getters, visionaries, game-changers, risk-takers, grinders, athletes, mentors, pioneers, self-starters, doers, warriors, strivers, competitors, underdogs, MVPs, trainers, founders, disruptors, the relentless, the passionate, the driven, the fearless, the dedicated, the unstoppable, ';
// Tile the phrase enough times to fill `.auth-wordwall` (which is 140% of the
// viewport thanks to `inset: -20%`) at the smaller `clamp(40px, 6vh, 80px)` font.
const WORDWALL_TEXT = WORDWALL_PHRASE.repeat(12);

// After a Firebase sign-in we navigate straight to '/'. PrivateRoute reads
// AuthContext.userMissing to detect the orphan case (Firebase auth succeeded
// but no backend Users row — e.g. abandoned social signup) and redirects to
// /signup?from=login. We do not call /me from here so the "Signing in…"
// spinner ends as soon as the Firebase session is established.

export default function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [providerLoading, setProviderLoading] = useState(null); // 'google' | 'apple' | null
  const [bgColor] = useState(() => DOT_COLORS[Math.floor(Math.random() * DOT_COLORS.length)]);
  const navigate = useNavigate();

  // Safari fallback path: signInWithRedirect resolves on next mount, not via
  // the popup callback. consumePendingRedirectResult returns the credential
  // if a redirect just completed; otherwise null (typical page load).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await consumePendingRedirectResult();
        if (cancelled || !result?.user) return;
        navigate('/');
      } catch (err) {
        if (cancelled) return;
        setError(translateAuthError(err));
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const persistKeepLoggedIn = () => {
    if (keepLoggedIn) {
      localStorage.setItem('keepLoggedIn', 'true');
      localStorage.setItem('loginTime', String(Date.now()));
    } else {
      localStorage.removeItem('keepLoggedIn');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Server resolves email-or-phone identifier to the user's email,
      // verifies the password, and returns a Firebase custom token. We
      // never see the resolved email — no phone→email enumeration vector.
      const { customToken } = await ApiService.login({
        identifier: identifier.trim(),
        password,
      });
      await signInWithCustomToken(auth, customToken);
      persistKeepLoggedIn();
      navigate('/');
    } catch (err) {
      setError('Incorrect email or password.');
      setLoading(false);
    }
  };

  const handleProvider = async (providerName) => {
    setError('');
    setProviderLoading(providerName);
    try {
      const fn = providerName === 'google' ? signInWithGoogle : signInWithApple;
      const result = await fn();
      // result is null on Safari (redirect path); consumePendingRedirectResult
      // on next mount finishes the flow. Popup path resolves here.
      if (result?.user) {
        persistKeepLoggedIn();
        navigate('/');
      }
    } catch (err) {
      setError(translateAuthError(err));
      setProviderLoading(null);
    }
  };

  const busy = loading || !!providerLoading;

  return (
    <div className="login-bg" style={{ backgroundColor: bgColor }}>
      <div className="auth-wordwall" aria-hidden="true">{WORDWALL_TEXT}</div>
      <a href="https://polyp.me" className="login-brand"><em>Polyp.</em></a>

      <div className="login-card">
        <h1 className="login-title">Login</h1>
        <p className="welcome">Welcome back,</p>

        {error && <div className="alert error">{error}</div>}

        <div className="provider-buttons">
          <button
            type="button"
            className="btn provider google"
            onClick={() => handleProvider('google')}
            disabled={busy}
          >
            <GoogleIcon />
            <span>{providerLoading === 'google' ? 'Signing in…' : 'Continue with Google'}</span>
          </button>
          <button
            type="button"
            className="btn provider apple"
            onClick={() => handleProvider('apple')}
            disabled={busy}
          >
            <AppleIcon />
            <span>{providerLoading === 'apple' ? 'Signing in…' : 'Continue with Apple'}</span>
          </button>
        </div>

        <div className="divider"><span>or sign in with email or phone</span></div>

        <form onSubmit={handleSubmit} className="form">
          <div className="field">
            <input
              type="text"
              placeholder="your@email.com or phone number"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              disabled={busy}
            />
          </div>

          <div className="field field-password">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={busy}
            />
            {password && (
              <button type="button" className="eye-toggle" onClick={() => setShowPassword(v => !v)} tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            )}
          </div>

          <div className="checkbox-group">
            <input
              type="checkbox"
              id="keep-logged-in"
              checked={keepLoggedIn}
              onChange={(e) => setKeepLoggedIn(e.target.checked)}
              disabled={busy}
            />
            <label htmlFor="keep-logged-in">
              Keep me logged in for 7 days
            </label>
          </div>

          <button className="btn primary" disabled={busy} type="submit">
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="divider"><span>New to Polyp?</span></div>

        <div className="new-to-polyp">
          <Link to="/signup" className="signup-link">Create Your Account</Link>
        </div>

        <div style={{ textAlign: "center", marginTop: 12, fontSize: 12 }}>
          <Link to="/claim" style={{ color: "rgba(255,255,255,0.5)" }}>
            Have a phone number on file with a business? Claim your account
          </Link>
        </div>
      </div>

    </div>
  );
}

function translateAuthError(err) {
  const code = err?.code || '';
  if (code === 'auth/popup-closed-by-user') return 'Sign-in cancelled.';
  if (code === 'auth/popup-blocked') return 'Pop-up was blocked. Allow pop-ups and try again.';
  if (code === 'auth/account-exists-with-different-credential') {
    return 'An account already exists with this email but a different sign-in method. Sign in with that method, then link this one in Settings.';
  }
  if (code === 'auth/operation-not-supported-in-this-environment') {
    return 'Sign-in not supported in this browser. Try a different browser.';
  }
  if (code === 'auth/network-request-failed') return 'Network error. Check your connection and try again.';
  return 'Sign-in failed. Please try again.';
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#EA4335" d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46 1.08 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"/>
      <path fill="#4285F4" d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.1.83-.64 2.08-1.84 2.92l2.84 2.2c1.7-1.57 2.68-3.88 2.68-6.62z"/>
      <path fill="#FBBC05" d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.76.53-1.78.9-3.12.9-2.38 0-4.4-1.57-5.12-3.74L.97 13.04C2.45 15.98 5.48 18 9 18z"/>
    </svg>
  );
}

function AppleIcon() {
  // Apple logo path scaled to fit a 16-unit viewBox with no clipping.
  // The body of the apple sits at y≈4–15, the leaf at y≈0–3 — fully
  // contained, unlike the previous path which extended outside `0 0 18 18`
  // and got cropped at the top and bottom of the button. HIG-compliant
  // proportions; render at any size by scaling width+height together.
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516.024.034 1.52.087 2.475-1.258.955-1.345.762-2.391.728-2.43Zm3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422.212-2.189 1.675-2.789 1.698-2.854.023-.065-.597-.79-1.254-1.157a3.692 3.692 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56.244.729.625 1.924 1.273 2.796.576.984 1.34 1.667 1.659 1.899.319.232 1.219.386 1.843.067.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758.347-.79.505-1.217.473-1.282Z"/>
    </svg>
  );
}
